import { getChronicleDeviceProps } from '@/utils/analytics/chronicle-device-props';
import { sessionOriginForSourceType } from '@/utils/analytics/session-source-type';
import { chronicleLogger } from '@/utils/logger/chronicle-logger';
import { logger } from '@/utils/logger/logger';
import { type CustomLogger } from '@/utils/logger/types';

export type HardwareDatadogLogLevel = 'info' | 'warn' | 'error';

export type HardwareDatadogAttributeValue =
  | string
  | number
  | boolean
  | ReadonlyArray<unknown>
  | Readonly<Record<string, unknown>>;

export type HardwareDatadogAttributes = Record<
  string,
  HardwareDatadogAttributeValue | null | undefined
>;

function cleanAttributes(
  attributes: HardwareDatadogAttributes
): Record<string, HardwareDatadogAttributeValue> {
  const cleaned: Record<string, HardwareDatadogAttributeValue> = {};
  for (const [key, value] of Object.entries(attributes)) {
    if (value === undefined || value === null) continue;
    // `session_id` on desktop logs is already taken by the Datadog RUM session,
    // so the Heidi server session id ships as `heidi_session_id` — the same
    // rename iOS applies in its Datadog sink, and the dashboards' join key.
    cleaned[key === 'session_id' ? 'heidi_session_id' : key] = value;
  }
  // Derived here rather than at ~20 call sites so it also lands on the payloads
  // Rust forwards verbatim (`chronicle:observability`, `bulk_download:observability`),
  // which no TypeScript caller can reach.
  if (cleaned.session_origin === undefined) {
    const origin = sessionOriginForSourceType(
      typeof cleaned.session_source_type === 'string'
        ? cleaned.session_source_type
        : undefined
    );
    if (origin !== undefined) cleaned.session_origin = origin;
  }
  return cleaned;
}

function emitAtLevel(
  child: CustomLogger,
  eventName: string,
  level: HardwareDatadogLogLevel
): void {
  switch (level) {
    case 'warn':
      child.warn(eventName);
      break;
    case 'error':
      child.error(eventName);
      break;
    default:
      child.info(eventName);
  }
}

/**
 * Shared (non-hardware) session-pipeline Datadog log — desktop-mic chunk
 * uploads etc. Uses the plain logger so `module:chronicle` stays a
 * Remote-only filter; same `session_id` → `heidi_session_id` rename.
 */
export function logSessionPipelineDatadogEvent(
  eventName: string,
  attributes: HardwareDatadogAttributes = {},
  level: HardwareDatadogLogLevel = 'info'
): void {
  const child =
    typeof logger.child === 'function'
      ? logger.child(cleanAttributes(attributes))
      : logger;
  emitAtLevel(child, eventName, level);
}

export function logHardwareDatadogEvent(
  eventName: string,
  attributes: HardwareDatadogAttributes = {},
  level: HardwareDatadogLogLevel = 'info'
): void {
  const device = getChronicleDeviceProps();
  // The session-pipeline dashboards facet on top-level @hardware_fw_version /
  // @hardware_serial_number (iOS logs them flat), so the two identity keys are
  // lifted out of the nested heidi_remote_device snapshot.
  const deviceIdentity: Record<string, HardwareDatadogAttributeValue> = {};
  if (device.hardware_fw_version !== undefined) {
    deviceIdentity.hardware_fw_version = device.hardware_fw_version;
  }
  if (device.hardware_serial_number !== undefined) {
    deviceIdentity.hardware_serial_number = device.hardware_serial_number;
  }
  const logger = chronicleLogger.child({
    ...deviceIdentity,
    ...cleanAttributes(attributes),
    ...(Object.keys(device).length > 0 ? { heidi_remote_device: device } : {}),
    hardware_datadog_only: true,
  });

  switch (level) {
    case 'warn':
      logger.warn(eventName);
      break;
    case 'error':
      logger.error(eventName);
      break;
    default:
      logger.info(eventName);
  }
}
